// The lazily loaded pages whose HTML is prerendered with real content
// (scripts/prerender-routes.mjs, BODY_MARKUP), and the preload that lets the
// first render show them instead of a route spinner.
//
// On those routes the visitor is already looking at the static page when the
// app bundle runs. If the first commit were the Suspense fallback, the static
// page would be swapped for a spinner and then for the same page again
// (measured 2026-10-01: about 300ms of spinner). bootstrap.ts therefore
// awaits preloadRoute(location.pathname) before createRoot().render(), capped
// at PRELOAD_CAP_MS so a slow or failed chunk can never strand the visitor on
// the static page: past the cap it renders anyway and the route falls back
// to the ordinary lazy path.
//
// Routes with an EMPTY #root are deliberately not preloaded: there the
// spinner is the first thing on screen, and holding the first render would
// replace it with a blank page.
//
// "/" is not here because HomePage is in the entry bundle already.
// src/__tests__/routePreload.test.ts pins this table to BODY_MARKUP.
import { lazyWithPreload, type PreloadableComponent } from './lib/lazyWithPreload'

export const BracketPage = lazyWithPreload(() => import('./pages/BracketPage'))
export const BuildPage = lazyWithPreload(() => import('./pages/BuildPage'))
export const AboutPage = lazyWithPreload(() => import('./pages/AboutPage'))
export const AccuracyPage = lazyWithPreload(() => import('./pages/AccuracyPage'))
export const CheckerPage = lazyWithPreload(() => import('./pages/CheckerPage'))
export const PrivacyPage = lazyWithPreload(() => import('./pages/PrivacyPage'))
export const TermsPage = lazyWithPreload(() => import('./pages/TermsPage'))
export const SupportPage = lazyWithPreload(() => import('./pages/SupportPage'))
export const PartnersPage = lazyWithPreload(() => import('./pages/PartnersPage'))

/** Only preload() is read from a route entry, so that is all the type asks for. */
type Preloadable = Pick<PreloadableComponent<object>, 'preload'>

export const PRERENDERED_ROUTES: Readonly<Record<string, Preloadable>> = {
  '/bracket': BracketPage,
  '/build': BuildPage,
  '/about': AboutPage,
  '/accuracy': AccuracyPage,
  '/checker': CheckerPage,
  '/privacy': PrivacyPage,
  '/terms': TermsPage,
  '/support': SupportPage,
  '/partners': PartnersPage,
}

export const PRELOAD_CAP_MS = 3000

/**
 * Preload the current route's page chunk, settling within capMs no matter
 * what. null when the path has nothing to preload, so the caller can keep
 * its first render synchronous.
 */
export function preloadRoute(
  pathname: string,
  capMs = PRELOAD_CAP_MS,
  routes: Readonly<Record<string, Preloadable>> = PRERENDERED_ROUTES,
): Promise<void> | null {
  const page = routes[pathname]
  if (!page) return null
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, capMs)
    page.preload().then(
      () => { clearTimeout(timer); resolve() },
      // A failed chunk renders anyway: the lazy path retries the import and
      // the route error boundary handles a second failure, as before.
      () => { clearTimeout(timer); resolve() },
    )
  })
}
